// "Run Brand AI Presence Analysis" — pick which of the brand's products to
// analyze, then start. The AI Presence sibling of ConsumerVoice/BrandCvRunForm
// (internal Brands cards → admin endpoints, network Catalog > Brands → rmn
// endpoints). Products with a fresh benchmark run are reused, not re-run.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { apiErrorCode } from '../../utils/apiError';
import { AipScoreChip } from './AipScoreChip';
import { BORDER, FONT, GOLD, GOLD_DARK, TEXT_FAINT } from '../ProposalFlow/theme';

const ERROR = 'var(--down)';

export interface BrandAipTarget {
  desireid: number;
  title: string | null;
  url: string | null;
  has_pdp: boolean;
  latest_score: number | null;
  last_scored_at: string | null;
}

interface Props {
  brandName: string;
  // GET → BrandAipTarget[]; POST { desireids? } → { id }.
  targetsEndpoint: string;
  runEndpoint: string;
  // The plan's per-run cap (DL_AIP_BRAND_MAX_PRODUCTS, default 10).
  maxProducts?: number;
  onCreated: (id: number) => void;
  // What to offer when the brand has no products at all.
  emptyAction?: { label: string; onClick: () => void };
}

export function BrandAipRunForm({ brandName, targetsEndpoint, runEndpoint, maxProducts = 10, onCreated, emptyAction }: Props) {
  const [targets, setTargets] = useState<BrandAipTarget[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstant.get(targetsEndpoint);
        if (cancelled) return;
        const rows = (Array.isArray(res.data) ? res.data : res.data?.data || []) as BrandAipTarget[];
        setTargets(rows);
        // Preselect what a plain run would take: the first `maxProducts`.
        setSelected(new Set(rows.slice(0, maxProducts).map((r) => r.desireid)));
      } catch {
        if (!cancelled) setLoadError("Couldn't load this brand's products. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetsEndpoint, maxProducts]);

  const allSelected = useMemo(
    () => !!targets && targets.length > 0 && selected.size === targets.length,
    [targets, selected],
  );
  const overCap = selected.size > maxProducts;

  const toggle = (desireid: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(desireid)) next.delete(desireid);
      else next.add(desireid);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !targets || selected.size === 0 || overCap) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const { data } = await axiosInstant.post(runEndpoint, { desireids: [...selected] });
      toast.success(`AI Presence analysis for ${brandName} started — it analyzes each product and takes a while.`);
      onCreated(data?.id);
    } catch (err: any) {
      const status = err?.response?.status;
      const code = apiErrorCode(err);
      let message = 'Something went wrong starting the analysis. Please try again.';
      if (status === 409 || code === 'AiPresenceAlreadyInProgress') {
        message = 'A AI Presence analysis is already running for this brand.';
      } else if (status === 429) {
        message = "You've reached the daily limit for AI Presence analyses. Please try again tomorrow.";
      } else if (code === 'BrandNotFound') {
        message = 'That brand is no longer available. Refresh the page and try again.';
      }
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) return <div style={{ fontFamily: FONT, fontSize: 14, color: ERROR }}>{loadError}</div>;
  if (targets === null) {
    return <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT }}>Loading {brandName}&apos;s products…</div>;
  }

  if (targets.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT, lineHeight: 1.6 }}>
          {brandName} has no products yet, so there is nothing to analyze. Add a product to the brand first.
        </div>
        {emptyAction && (
          <button
            type="button"
            onClick={emptyAction.onClick}
            className="self-start px-[18px] py-[9px] rounded-[9px] text-[13px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors"
          >
            {emptyAction.label}
          </button>
        )}
      </div>
    );
  }

  const disabled = submitting || selected.size === 0 || overCap;
  let selectLabel = 'Select all';
  if (allSelected) selectLabel = 'Select none';
  else if (targets.length > maxProducts) selectLabel = `Select first ${maxProducts}`;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div style={{ fontFamily: FONT, fontSize: 13, color: TEXT_FAINT, lineHeight: 1.6, marginBottom: 12 }}>
        We&apos;ll ask Claude, Google Gemini and OpenAI the shopping questions buyers of each product you pick
        ask, and roll the results into one brand report. Up to {maxProducts} products per run.
      </div>

      <div className="flex items-center justify-between mb-2">
        <span className={`text-[13px] font-semibold ${overCap ? 'text-down' : 'text-text-1'}`}>
          {selected.size} of {targets.length} selected{overCap ? ` — pick at most ${maxProducts}` : ''}
        </span>
        <button
          type="button"
          onClick={() => setSelected(allSelected ? new Set() : new Set(targets.slice(0, maxProducts).map((t) => t.desireid)))}
          className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
        >
          {selectLabel}
        </button>
      </div>

      <div className="flex flex-col border border-border-token rounded-xl overflow-hidden mb-5 max-h-[320px] overflow-y-auto">
        {targets.map((t) => (
          <label
            key={t.desireid}
            className="flex items-center gap-3 px-3 py-2.5 border-b border-border-token last:border-0 cursor-pointer hover:bg-surface-2"
          >
            <input type="checkbox" checked={selected.has(t.desireid)} onChange={() => toggle(t.desireid)} className="shrink-0" />
            <span className="flex-1 min-w-0 flex flex-col">
              <span className="text-[13px] text-text-1 truncate" title={t.title || ''}>
                {t.title || `Product ${t.desireid}`}
              </span>
              <span className="text-[11px] text-muted truncate">
                {t.has_pdp ? 'Has a product page' : 'No product page yet'}
              </span>
            </span>
            {t.latest_score != null ? (
              <AipScoreChip score={t.latest_score} title="Latest AI Presence score for this product" />
            ) : (
              <span className="text-[11px] text-muted">not analyzed</span>
            )}
          </label>
        ))}
      </div>

      {formError && <div style={{ fontFamily: FONT, fontSize: 13, color: ERROR, marginBottom: 16 }}>{formError}</div>}

      <button
        type="submit"
        disabled={disabled}
        style={{
          width: '100%',
          padding: 14,
          borderRadius: 12,
          border: 'none',
          background: disabled ? BORDER : GOLD,
          color: disabled ? '#B5AFA5' : '#fff',
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = GOLD_DARK;
        }}
        onMouseLeave={(e) => {
          if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = GOLD;
        }}
      >
        {submitting ? 'Starting analysis…' : 'Run Brand AI Presence Analysis'}
      </button>
      <div style={{ fontFamily: FONT, fontSize: 12, color: TEXT_FAINT, marginTop: 10, textAlign: 'center' }}>
        Runs in the background — each product takes a minute or two.
      </div>
    </form>
  );
}
